import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { load } from "@cashfreepayments/cashfree-js";
import api, { extractErr, mediaUrl, storePublicBase } from "@/lib/api";
import { useMerchantAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowUpDown, Check, ChevronDown, ExternalLink, LayoutGrid, LayoutList, Layers, Loader2,
  Palette, Search, SlidersHorizontal, Sparkles,
} from "lucide-react";
import { themeFallbackClass } from "@/lib/storefrontTheme";
import { themeLabel, themeRequiresAddon } from "@/components/CarPartsOnboardDialog";

const PAGE_SIZE = 12;

const inr2 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BADGE_LABEL = {
  popular: "Popular",
  new: "New",
  premium: "Premium",
  trending: "Trending",
};

function waitMs(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitAtLeast(ms, promise) {
  const started = Date.now();
  const result = await promise;
  const left = ms - (Date.now() - started);
  if (left > 0) await waitMs(left);
  return result;
}

function SwitchingOverlay({ themeLabel }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4" data-testid="theme-switching-overlay">
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-500 px-6 py-8 text-white text-center">
          <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-white/15 flex items-center justify-center">
            <Sparkles className="w-7 h-7 animate-pulse" />
          </div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-white/80">Theme Store</div>
          <h2 className="mt-2 font-display text-2xl font-bold">Switching theme</h2>
          <p className="mt-2 text-sm text-white/85">Applying {themeLabel}. Please wait a few seconds…</p>
        </div>
        <div className="px-6 py-6 text-center">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-indigo-600" />
          <p className="mt-3 text-sm text-slate-500">Keep this window open. Your storefront will update shortly.</p>
        </div>
      </div>
    </div>
  );
}

function badgeTone(kind) {
  if (kind === "current") return "bg-emerald-600 text-white";
  if (kind === "premium") return "bg-stone-900 text-amber-200";
  if (kind === "popular") return "bg-rose-500 text-white";
  if (kind === "new") return "bg-emerald-500 text-white";
  if (kind === "trending") return "bg-violet-600 text-white";
  return "bg-indigo-600 text-white";
}

function FilterSelect({ icon: Icon, value, onChange, testId, children }) {
  return (
    <div className="relative min-w-[10.5rem]">
      <Icon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="h-11 w-full appearance-none rounded-full border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-700 shadow-sm outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
    </div>
  );
}

function ThemeCatalogCard({ th, compact, previewHref, busy, onSwitch }) {
  const overlayKind = th.active ? "current" : (th.badge || "");
  const overlayLabel = th.active ? "Current Theme" : (BADGE_LABEL[th.badge] || "");
  return (
    <article
      className={`group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ${compact ? "flex flex-row" : "flex flex-col"}`}
      data-testid={`appearance-card-${th.id}`}
    >
      <div className={`relative bg-slate-100 shrink-0 ${compact ? "w-40 sm:w-56 h-36 sm:h-44" : "aspect-[16/10]"}`}>
        {th.thumbnail_url ? (
          <img src={mediaUrl(th.thumbnail_url)} alt={th.label} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className={`absolute inset-0 ${themeFallbackClass(th.id)}`} />
        )}
        {overlayLabel ? (
          <span
            className={`absolute top-3 left-3 inline-flex items-center gap-1 rounded-md text-[11px] font-bold px-2.5 py-1 ${badgeTone(overlayKind)}`}
            data-testid={th.active ? `appearance-active-${th.id}` : `appearance-badge-${th.id}`}
          >
            {th.active ? <Check className="w-3.5 h-3.5" /> : null}
            {overlayLabel}
          </span>
        ) : null}
      </div>
      <div className={`p-4 sm:p-5 flex-1 flex flex-col ${compact ? "min-w-0" : ""}`}>
        <h3 className="font-display text-lg font-bold text-slate-900">{th.label}</h3>
        <p className="text-sm text-slate-500 mt-1 leading-relaxed line-clamp-2">{th.description}</p>
        {(th.tags || []).length ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(th.tags || []).slice(0, 3).map((tag) => (
              <span key={tag} className="text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{tag}</span>
            ))}
          </div>
        ) : null}
        <div className={`mt-auto pt-4 flex flex-wrap items-center gap-2 ${compact ? "justify-end" : ""}`}>
          <a href={previewHref} target="_blank" rel="noreferrer" className="inline-flex">
            <Button type="button" variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900 px-2" data-testid={`appearance-preview-${th.id}`}>
              Live preview <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </a>
          {th.active ? (
            <span className="inline-flex items-center h-9 rounded-full bg-sky-100 text-sky-700 text-sm font-semibold px-4">Current</span>
          ) : (
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => onSwitch(th.id)}
              className="rounded-full bg-orange-500 hover:bg-orange-600 text-white"
              data-testid={`appearance-switch-${th.id}`}
            >
              Switch Theme
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}

export default function AdminAppearance() {
  const { tenant } = useMerchantAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [switching, setSwitching] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("all");
  const [styleId, setStyleId] = useState("all");
  const [featureId, setFeatureId] = useState("all");
  const [sort, setSort] = useState("new");
  const [view, setView] = useState("grid");
  const [page, setPage] = useState(1);

  const loadAppearance = async () => {
    const { data: d } = await api.get("/merchant/appearance");
    setData(d);
    return d;
  };

  useEffect(() => {
    const cfOrderId = params.get("cf_order_id");
    const run = async () => {
      setLoading(true);
      try {
        if (cfOrderId) {
          setSwitching("your new theme");
          await waitAtLeast(6000, (async () => {
            const { data: v } = await api.get(`/merchant/appearance/verify/${cfOrderId}`);
            if (v.applied) toast.success("Theme switched successfully");
            else toast.error("Payment not completed yet. If you paid, the theme will update shortly.");
          })());
          params.delete("cf_order_id");
          setParams(params, { replace: true });
          setSwitching(null);
        }
        await loadAppearance();
      } catch (e) {
        toast.error(extractErr(e));
        setSwitching(null);
      } finally {
        setLoading(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewHref = (th) => {
    if (th.preview_url) return th.preview_url;
    if (tenant) return storePublicBase(tenant);
    return data?.slug ? `/${data.slug}` : "/";
  };

  const startSwitch = async (themeId, label) => {
    setBusy(true);
    setConfirmId(null);
    try {
      const { data: d } = await api.post("/merchant/appearance/checkout", { theme_id: themeId });
      if (d.free) {
        setSwitching(label);
        await waitAtLeast(6000, loadAppearance());
        setSwitching(null);
        toast.success(`${label} is now active`);
        return;
      }
      const cashfree = await load({ mode: d.sdk_mode || "sandbox" });
      await cashfree.checkout({ paymentSessionId: d.payment_session_id, redirectTarget: "_self" });
    } catch (e) {
      toast.error(extractErr(e));
      setSwitching(null);
    } finally {
      setBusy(false);
    }
  };

  const themes = data?.themes || [];
  const categories = data?.categories || [];
  const catById = Object.fromEntries(categories.map((c) => [c.id, c]));
  const styles = useMemo(() => {
    const seen = new Set();
    const out = [];
    themes.forEach((t) => {
      (t.tags || []).forEach((tag) => {
        const key = String(tag || "").trim();
        if (!key || seen.has(key.toLowerCase())) return;
        seen.add(key.toLowerCase());
        out.push(key);
      });
    });
    return out.sort((a, b) => a.localeCompare(b));
  }, [themes]);
  const features = useMemo(() => {
    const seen = new Set();
    const out = [];
    themes.forEach((t) => {
      const b = t.badge;
      if (!b || seen.has(b)) return;
      seen.add(b);
      out.push(b);
    });
    return out;
  }, [themes]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...themes];
    if (q) {
      list = list.filter((t) => {
        const cats = (t.category_ids || []).map((id) => catById[id]?.name || id).join(" ");
        const hay = [t.id, t.label, t.description, (t.tags || []).join(" "), t.badge, cats].join(" ").toLowerCase();
        return hay.includes(q);
      });
    }
    if (categoryId !== "all") {
      list = list.filter((t) => (t.category_ids || []).includes(categoryId));
    }
    if (styleId !== "all") {
      list = list.filter((t) => (t.tags || []).some((tag) => String(tag).toLowerCase() === styleId.toLowerCase()));
    }
    if (featureId !== "all") {
      list = list.filter((t) => t.badge === featureId);
    }
    if (sort === "az") list = [...list].sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    else if (sort === "popular") {
      list = [...list].sort((a, b) => {
        const score = (t) => (t.active ? 3 : 0) + (t.badge === "popular" ? 2 : 0) + (t.badge === "premium" ? 1 : 0);
        return score(b) - score(a);
      });
    } else {
      list = [...list].sort((a, b) => {
        const score = (t) => (t.badge === "new" ? 2 : 0) + (t.badge === "premium" ? 1 : 0);
        return score(b) - score(a) || (a.label || "").localeCompare(b.label || "");
      });
    }
    return list;
  }, [themes, query, categoryId, styleId, featureId, sort, catById]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const slice = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const start = filtered.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const end = Math.min(safePage * PAGE_SIZE, filtered.length);

  const setFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="appearance-page">
      {switching ? <SwitchingOverlay themeLabel={switching} /> : null}
      <div className="flex flex-col lg:flex-row lg:items-stretch gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2" data-testid="appearance-title">
            <span className="w-10 h-10 rounded-2xl bg-indigo-50 text-indigo-600 inline-flex items-center justify-center">
              <Palette className="w-5 h-5" />
            </span>
            Theme Store
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            Choose a beautifully designed theme to give your store the perfect look and feel.
            {themes.length ? ` ${themes.length}+ professional themes.` : " 100+ professional themes."} Switch anytime. No coding required.
          </p>
        </div>
        <div className="lg:w-[22rem] rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white px-5 py-4 flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-white text-indigo-600 shadow-sm inline-flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </span>
          <div>
            <div className="font-semibold text-slate-900">Your store, your style</div>
            <p className="text-sm text-slate-500 mt-0.5">Find the perfect theme and make it yours.</p>
          </div>
        </div>
      </div>

      {loading && !data ? (
        <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading themes…</div>
      ) : (
        <>
          <div className="flex flex-col xl:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                placeholder="Search themes by name, style, or keyword..."
                className="h-11 w-full rounded-full border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-800 shadow-sm outline-none placeholder:text-slate-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
                data-testid="appearance-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <FilterSelect icon={LayoutGrid} value={categoryId} onChange={setFilter(setCategoryId)} testId="appearance-filter-category">
                <option value="all">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </FilterSelect>
              <FilterSelect icon={Layers} value={styleId} onChange={setFilter(setStyleId)} testId="appearance-filter-style">
                <option value="all">All Styles</option>
                {styles.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </FilterSelect>
              <FilterSelect icon={SlidersHorizontal} value={featureId} onChange={setFilter(setFeatureId)} testId="appearance-filter-feature">
                <option value="all">All Features</option>
                {features.map((f) => (
                  <option key={f} value={f}>{BADGE_LABEL[f] || f}</option>
                ))}
              </FilterSelect>
              <FilterSelect icon={ArrowUpDown} value={sort} onChange={setFilter(setSort)} testId="appearance-sort">
                <option value="new">Newest First</option>
                <option value="popular">Most Popular</option>
                <option value="az">A–Z</option>
              </FilterSelect>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500" data-testid="appearance-showing">
              Showing {start}–{end} of {filtered.length} themes
            </p>
            <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="appearance-view-toggle">
              <button
                type="button"
                onClick={() => setView("grid")}
                className={`h-8 px-3 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${view === "grid" ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-800"}`}
                data-testid="appearance-view-grid"
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Grid
              </button>
              <button
                type="button"
                onClick={() => setView("list")}
                className={`h-8 px-3 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${view === "list" ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-800"}`}
                data-testid="appearance-view-list"
              >
                <LayoutList className="w-3.5 h-3.5" /> List
              </button>
            </div>
          </div>

          {slice.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 py-16 text-center text-slate-500" data-testid="appearance-empty">
              No themes match this search or filter.
            </div>
          ) : (
            <div
              className={view === "list" ? "space-y-4" : "grid sm:grid-cols-2 xl:grid-cols-4 gap-5"}
              data-testid="appearance-theme-grid"
            >
              {slice.map((th) => (
                <ThemeCatalogCard
                  key={th.id}
                  th={th}
                  compact={view === "list"}
                  previewHref={previewHref(th)}
                  busy={busy}
                  onSwitch={setConfirmId}
                />
              ))}
            </div>
          )}

          {pages > 1 ? (
            <div className="flex items-center justify-center gap-2" data-testid="appearance-pagination">
              {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPage(n)}
                  className={`w-9 h-9 rounded-lg text-sm font-semibold ${n === safePage ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600"}`}
                >
                  {n}
                </button>
              ))}
            </div>
          ) : null}

          {confirmId ? (() => {
            const th = (data?.themes || []).find((x) => x.id === confirmId);
            const extra = themeRequiresAddon(confirmId) ? data?.required_addons?.[confirmId] : null;
            const quote = extra?.switch_quote;
            const addonLines = (!extra?.owned && quote?.addons) || [];
            const payTotal = addonLines.length ? quote.total : data?.total;
            const gstPercent = addonLines.length ? quote.gst_percent : data?.gst_percent;
            return (
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4" data-testid="appearance-confirm">
              <Card className="w-full max-w-md p-6 space-y-4">
                <h2 className="font-display text-xl font-bold text-slate-900">Switch theme</h2>
                {addonLines.length ? (
                  <div className="text-sm text-slate-600 space-y-3" data-testid="appearance-addon-quote">
                    <p>{themeLabel(confirmId)} needs {addonLines.length === 1 ? "a compulsory addon" : "compulsory addons"} you have not purchased yet. The theme switch price includes them.</p>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1">
                      <div className="flex justify-between"><span>Theme switch</span><span>₹{inr2(quote.theme_amount)}</span></div>
                      {addonLines.map((row) => (
                        <div key={row.id} className="flex justify-between" data-testid={`appearance-addon-line-${row.id}`}>
                          <span>{row.name}</span><span>₹{inr2(row.price)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between"><span>GST ({gstPercent}%)</span><span>₹{inr2(quote.gst_amount)}</span></div>
                      <div className="flex justify-between font-semibold text-slate-900 pt-1 border-t border-dashed border-slate-300">
                        <span>Total</span><span data-testid="appearance-pay-total">₹{inr2(payTotal)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600">
                    Changing your storefront theme costs ₹{inr2(data?.switch_price)} + GST ({data?.gst_percent}%).
                    Payable now: <span className="font-semibold">₹{inr2(data?.total)}</span>.
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setConfirmId(null)}>Cancel</Button>
                  <Button
                    type="button"
                    disabled={busy}
                    data-testid="appearance-confirm-pay"
                    onClick={() => startSwitch(confirmId, th?.label || confirmId)}
                  >
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Pay ₹{inr2(payTotal)}
                  </Button>
                </div>
              </Card>
            </div>
            );
          })() : null}
        </>
      )}
    </div>
  );
}
